import React, { useEffect, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import NotesCategoryCard, { NotesEntry } from '../../components/NotesCategoryCard';
import NotesViewerDialog from '../../components/NotesViewerDialog';
import { NoContentMessage, NoPermissionMessage } from '../../components/PageLayout';
import SkeletonStack from '../../components/SkeletonStack';
import Tabs from '../../components/Tabs';
import { useGetNotesCategoriesQuery } from '../../services/notes';
import { PermissionType } from '../../types/common';
import {
  SubjectDetails, SUBJECTS,
} from '../../utils/common/constants';
import getSubjectsFromPermissions from '../../utils/common/getSubjectsFromPermissions';
import useUserPermissions from '../../utils/react/hooks/useUserPermissions';

import {
  CategoriesContainer, NotesCategoryCardContainer, SkeletonContainer,
} from './index.styles';

const NotesPage = () => {
  // Get parameters
  const [searchParams, setSearchParams] = useSearchParams();
  const permissions = useUserPermissions();
  const querySubject = searchParams.get('subject');
  const queryCategory = searchParams.get('category');
  const queryNote = searchParams.get('note');

  const [selectedNote, setSelectedNote] = useState<NotesEntry | null>(null);
  const [subjectsList, setSubjectsList] = useState<SubjectDetails[] | null>(
    permissions ? getSubjectsFromPermissions(permissions, PermissionType.Note) : null,
  );
  const [subject, setSubject] = useState(querySubject || subjectsList?.[0]?.key || '');
  const [isPermissionError, setIsPermissionError] = useState(false);

  // Fetch data
  const { data: categories, isFetching, isError, error } = useGetNotesCategoriesQuery(
    { subject }, { skip: !subject },
  );

  useEffect(() => {
    const updateSubjects = async () => {
      if (permissions) {
        const list = await getSubjectsFromPermissions(permissions, PermissionType.Note);
        setSubjectsList(list);
        setSubject(querySubject || list[0]?.key);
        if (list.length === 0 && !isFetching) {
          setIsPermissionError(true);
        }
      }
    };
    updateSubjects();
  }, [permissions]);

  const clearParams = () => {
    setSearchParams({}, { replace: true });
  };

  const handleNoteClick = (notes: NotesEntry) => {
    // Google Drive files can't be embedded, so should open in a new tab
    if (notes.isExternalLink) {
      window.open(notes.href, '_blank');
    } else {
      setSelectedNote(notes);
    }
  };

  const handleNoteClose = () => {
    setSelectedNote(null);
    clearParams();
  };

  useEffect(() => {
    if (error) setIsPermissionError(error && 'status' in error && error.status === 401);
  }, [isError]);

  // If note is supplied in query params, open the notes viewer dialog
  useEffect(() => {
    if (subject && categories && subjectsList) {
      if (queryNote) {
      // Find the category that matches the queryCategory
        const category = categories?.data?.find((c) => c.category === queryCategory);

        // Find the note in the category that matches the queryNote
        const note = category?.notes.find((n) => n.key === queryNote);

        if (note) {
          handleNoteClick(note);
        }
      }
    }
  }, [subject, categories, subjectsList]);

  if (subjectsList && subjectsList?.length === 0) {
  // If subjectsList has loaded but there are no subjects, show no permission message
    return (
      <NoPermissionMessage />
    );
  }

  if (!subjectsList || !categories || !permissions) {
    return (
      <SkeletonStack />
    );
  }

  return (
    <>
      <Tabs
        options={subjectsList}
        selectedKey={subject}
        onChange={(newKey) => {
          setSubject(newKey);
          clearParams();
        }}
      />
      <CategoriesContainer>
        {!isFetching && isPermissionError && <NoPermissionMessage />}
        {!isFetching && !isPermissionError && (
          ((categories?.data || []).length > 0) ?
            [...(categories.data)].sort((a, b) => a.label.localeCompare(b.label)).map((c) => (
              <NotesCategoryCardContainer
                key={`${c.subject}#${c.category}`}
              >
                <NotesCategoryCard
                  color={SUBJECTS[subject]?.colorKey}
                  categoryLabel={c.label}
                  notes={c.notes}
                  onNoteClick={(note) => {
                    handleNoteClick(note);
                    clearParams();
                  }}
                />
              </NotesCategoryCardContainer>
            )) : <NoContentMessage />
        )}
        {(isFetching && !isPermissionError) && (
          <SkeletonContainer>
            <SkeletonStack />
          </SkeletonContainer>
        )}
      </CategoriesContainer>
      <NotesViewerDialog
        open={selectedNote !== null}
        handleClose={handleNoteClose}
        href={selectedNote?.href || ''}
        label={selectedNote?.label || 'Cognito Resource'}
        shortLabel={selectedNote?.shortLabel || 'Resource'}
      />
    </>
  );
};
export default NotesPage;
